import React, { useContext, useMemo, useState } from "react";
import axios from "axios";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Avatar,
  Grid,
  Divider,
  Chip,
  Alert,
  IconButton,
  Tooltip,
  Link,
  SvgIcon,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import InstagramIcon from "@mui/icons-material/Instagram";
import FacebookIcon from "@mui/icons-material/Facebook";
import LinkedInIcon from "@mui/icons-material/LinkedIn";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import { LoadingButton } from "@mui/lab";
import { useParams, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import MuPb from "../../widgets/MuPb";
import { getErrorResponse, toastError } from "../../utils/util";
import useApiEp from "../../hooks/useApiEp";
import BackBtn from "../../widgets/BackBtn";
import { UserContext } from "../../context/UserProvider";
import { API_ENDPOINT } from "../../utils/constants";
import { getHeader } from "../../utils/utils2";
import { useDialog } from "../dialogs/DialogProvider";
import { DIALOG_TYPES } from "../dialogs/CommonDialog";
import { canResendVisitorRegistrationEmail } from "../../utils/accessControl";

// Utility function to convert labels into object keys
function toCamelCase(str) {
  if (!str) return "";
  return str
    .replace(/[^a-zA-Z0-9\s_-]/g, "")
    .toLowerCase()
    .replace(/(?:^\w|[A-Z]|\b\w)/g, (match, index) =>
      index === 0 ? match.toLowerCase() : match.toUpperCase()
    )
    .replace(/\s+|[-_]+/g, "");
}

// Styled components
const BannerContainer = styled(Box)(({ theme, banner }) => ({
  position: "relative",
  height: 250,
  backgroundColor: theme.palette.grey[300],
  backgroundImage: banner ? `url(${banner})` : "none",
  backgroundSize: "cover",
  backgroundPosition: "center",
  "&::after": {
    content: '""',
    position: "absolute",
    bottom: 0,
    left: 0,
    right: 0,
    height: "120px",
    background: "linear-gradient(to top, rgba(0,0,0,0.6) 0%, transparent 100%)",
  },
}));

const ProfileImageContainer = styled(Box)(({ theme }) => ({
  position: "relative",
  marginTop: -60,
  marginLeft: theme.spacing(4),
  zIndex: 2,
}));

const ProfileAvatar = styled(Avatar)(({ theme }) => ({
  width: 120,
  height: 120,
  border: `4px solid ${theme.palette.background.paper}`,
  boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
  backgroundColor: "#fff",
}));

const XIcon = (props) => (
  <SvgIcon {...props} viewBox="0 0 16 16">
    <path d="M12.6.75h2.454l-5.36 6.142L16 15.25h-4.937l-3.867-5.07-4.425 5.07H.316l5.733-6.57L0 .75h5.063l3.495 4.633L12.601.75Zm-.86 13.028h1.36L4.323 2.145H2.865z" />
  </SvgIcon>
);

const VisitorProfileDetail = () => {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const { user: signedInUser } = useContext(UserContext);
  const { showDialog } = useDialog();
  const [resendingEmail, setResendingEmail] = useState(false);

  // Get the pendingApproval param
  const pendingApproval = searchParams.get("pendingApproval");
  const isPendingApprovalMode = pendingApproval === "true";

  // Dynamically build the endpoint
  const endpoint = `/admin/get-profile-view-by-id/${id}${
    pendingApproval ? `?pendingApproval=${pendingApproval}` : ""
  }`;

  const { data, isLoading, isError, error } = useApiEp(endpoint);

  const eD = useMemo(
    () => ({
      productCategories: data?.productCategories,
      type: data?.type,
      defProfileBanner: data?.defProfileBanner,
    }),
    [data]
  );

  const user = useMemo(() => data?.user || data || {}, [data]);
  const formSchema = useMemo(() => data?.form || [], [data]);
  const canResendEmail = canResendVisitorRegistrationEmail(
    signedInUser?.role,
    user?.role
  );

  const handleResendEmail = () => {
    showDialog({
      type: DIALOG_TYPES.CONFIRM_WARNING,
      title: "Resend registration email?",
      message: `A new registration email will be sent to ${
        user.email || "this visitor"
      }.`,
      buttonText: "Yes, resend",
      onConfirm: async () => {
        try {
          setResendingEmail(true);
          const { data: response } = await axios.post(
            `${API_ENDPOINT}/admin/resend-visitor-registration-email/${id}`,
            {},
            getHeader()
          );
          toast.success(response.message);
        } catch (resendError) {
          toastError(resendError);
        } finally {
          setResendingEmail(false);
        }
      },
    });
  };

  if (isLoading) {
    return <MuPb />;
  }

  if (isError) {
    return (
      <Alert severity="error">
        {error ? getErrorResponse(error) : "Failed to load visitor data"}
      </Alert>
    );
  }

  const socialLinks = [
    { key: "in", icon: <InstagramIcon />, label: "Instagram", url: user.in },
    { key: "x", icon: <XIcon />, label: "X (Twitter)", url: user.x },
    { key: "fb", icon: <FacebookIcon />, label: "Facebook", url: user.fb },
    { key: "ln", icon: <LinkedInIcon />, label: "LinkedIn", url: user.ln },
  ];

  return (
    <Box>
      <BackBtn />
      <Box sx={{ maxWidth: 850, mx: "auto", my: 4 }}>
        <Card
          sx={{
            borderRadius: 4,
            boxShadow: "0 10px 40px rgba(0,0,0,0.06)",
            overflow: "hidden",
            border: "1px solid #eef2f6",
          }}
          elevation={0}
        >
          {/* Banner */}
          <BannerContainer
            banner={user?.profileBanner || eD?.defProfileBanner}
          />

          {/* Profile Image */}
          <ProfileImageContainer>
            <ProfileAvatar src={user.profileImage} alt={user.firstName}>
              {user.firstName?.charAt(0)}
            </ProfileAvatar>
          </ProfileImageContainer>

          {/* Core Profile Header Details */}
          <CardContent sx={{ px: { xs: 3, sm: 5 }, pb: { xs: 4, sm: 6 } }}>
            <Box sx={{ mt: 1, mb: 4 }}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  flexWrap: "wrap",
                  gap: 1.5,
                }}
              >
                <Typography
                  variant="h4"
                  sx={{
                    fontWeight: 800,
                    color: "#1e293b",
                    letterSpacing: "-0.02em",
                  }}
                >
                  {user.title} {user.firstName} {user.lastName}
                </Typography>

                {/* Read-only status indicator; approval action itself now lives
                    in the Manage Trade Delegates list */}
                {isPendingApprovalMode && (
                  <Chip
                    icon={
                      <CheckCircleIcon
                        sx={{
                          color: user.approved
                            ? "#16a34a !important"
                            : "#94a3b8 !important",
                        }}
                      />
                    }
                    label={user.approved ? "Approved" : "Pending Approval"}
                    size="small"
                    sx={{
                      fontWeight: 600,
                      backgroundColor: user.approved ? "#dcfce7" : "#f1f5f9",
                      color: user.approved ? "#166534" : "#64748b",
                    }}
                  />
                )}
              </Box>
              {user.designation && (
                <Typography
                  variant="subtitle1"
                  sx={{ fontWeight: 600, color: "#64748b", mt: 0.5, mb: 1 }}
                >
                  {user.designation}
                </Typography>
              )}
              {user.barcode_id && (
                <Chip
                  label={user.barcode_id}
                  color="primary"
                  size="small"
                  sx={{ mt: 1 }}
                />
              )}
              {canResendEmail && (
                <LoadingButton
                  loading={resendingEmail}
                  onClick={handleResendEmail}
                  variant="outlined"
                  size="small"
                  startIcon={<EmailOutlinedIcon />}
                  sx={{ mt: 1, ml: { sm: 1 }, textTransform: "none" }}
                >
                  Resend Registration Email
                </LoadingButton>
              )}
            </Box>

            <Divider sx={{ my: 4, borderColor: "#f1f5f9" }} />

            {/* Dynamically Generated Details Grid */}
            <Grid container spacing={4}>
              {formSchema.map((field) => {
                const label = field.properties?.label || "";
                const key = toCamelCase(label);
                const value = user[key];

                const mdGrid = parseInt(field.properties?.grid, 10) || 12;

                switch (field.type) {
                  case "Divider":
                    return (
                      <Grid item xs={12} key={field.id}>
                        <Divider sx={{ my: 1, borderColor: "#f1f5f9" }} />
                      </Grid>
                    );

                  case "PlainText":
                    return (
                      <Grid item xs={12} key={field.id}>
                        <Typography
                          variant={field.properties?.variant || "h6"}
                          sx={{ mt: 1, fontWeight: "bold", color: "#1e293b" }}
                        >
                          {label}
                        </Typography>
                      </Grid>
                    );

                  case "ParagraphDescription":
                    return <Grid item xs={12} key={field.id}></Grid>;

                  case "CountryStateCity":
                    return (
                      <Grid item xs={12} md={mdGrid} key={field.id}>
                        <Typography
                          variant="subtitle2"
                          sx={{
                            color: "#94a3b8",
                            fontWeight: 600,
                            textTransform: "uppercase",
                            letterSpacing: 0.5,
                            mb: 0.5,
                          }}
                        >
                          {label}
                        </Typography>
                        <Typography
                          variant="body1"
                          sx={{ color: "#334155", fontWeight: 500 }}
                        >
                          {[
                            user?.city?.name,
                            user?.state?.name,
                            user?.country?.name,
                          ]
                            .filter(Boolean)
                            .join(", ") || "-"}
                        </Typography>
                      </Grid>
                    );

                  case "ProductCategories":
                    return (
                      <Grid item xs={12} key={field.id}>
                        <Typography
                          variant="subtitle2"
                          sx={{
                            color: "#94a3b8",
                            fontWeight: 600,
                            textTransform: "uppercase",
                            letterSpacing: 0.5,
                            mb: 1.5,
                          }}
                        >
                          {label}
                        </Typography>
                        <Box
                          sx={{
                            display: "flex",
                            flexWrap: "wrap",
                            gap: 1,
                            mt: 1,
                          }}
                        >
                          {Array.isArray(value) && value.length > 0 ? (
                            value.map((cat, i) => {
                              let catLabel = cat.MainCategory || "";

                              if (
                                (eD.type === "double" ||
                                  eD.type === "triple") &&
                                cat.Category
                              ) {
                                catLabel += ` > ${cat.Category}`;
                              }

                              if (eD.type === "triple" && cat.SubCategory) {
                                catLabel += ` > ${cat.SubCategory}`;
                              }

                              return (
                                <Chip
                                  key={i}
                                  label={catLabel}
                                  variant="outlined"
                                  sx={{
                                    maxWidth: "100%",
                                    "& .MuiChip-label": {
                                      whiteSpace: "normal",
                                      lineHeight: 1.2,
                                      py: 0.5,
                                    },
                                  }}
                                />
                              );
                            })
                          ) : (
                            <Typography
                              variant="body2"
                              sx={{ color: "#64748b" }}
                            >
                              -
                            </Typography>
                          )}
                        </Box>
                      </Grid>
                    );

                  case "Checkbox":
                    return (
                      <Grid item xs={12} sm={6} md={mdGrid} key={field.id}>
                        <Typography
                          variant="subtitle2"
                          sx={{
                            color: "#94a3b8",
                            fontWeight: 600,
                            textTransform: "uppercase",
                            letterSpacing: 0.5,
                            mb: 0.5,
                          }}
                        >
                          {label}
                        </Typography>
                        <Typography
                          variant="body1"
                          sx={{ color: "#334155", fontWeight: 500 }}
                        >
                          {value === true ? "Yes" : "No"}
                        </Typography>
                      </Grid>
                    );

                  default:
                    // Standard Fields: TextField, Select, RadioGroup, etc.
                    return (
                      <Grid item xs={12} sm={6} md={mdGrid} key={field.id}>
                        <Typography
                          variant="subtitle2"
                          sx={{
                            color: "#94a3b8",
                            fontWeight: 600,
                            textTransform: "uppercase",
                            letterSpacing: 0.5,
                            mb: 0.5,
                          }}
                        >
                          {label}
                        </Typography>
                        <Typography
                          variant="body1"
                          sx={{ color: "#334155", fontWeight: 500 }}
                        >
                          {value !== undefined && value !== null && value !== ""
                            ? String(value)
                            : "-"}
                        </Typography>
                      </Grid>
                    );
                }
              })}

              {/* Social Media Links */}
              {socialLinks.some((link) => link.url) && (
                <Grid item xs={12}>
                  <Typography
                    variant="subtitle2"
                    sx={{
                      color: "#94a3b8",
                      fontWeight: 600,
                      textTransform: "uppercase",
                      letterSpacing: 0.5,
                      mb: 1.5,
                    }}
                  >
                    Social Media
                  </Typography>
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5 }}>
                    {socialLinks.map(({ key, icon, label, url }) =>
                      url ? (
                        <Tooltip key={key} title={label}>
                          <IconButton
                            component={Link}
                            href={url}
                            target="_blank"
                            rel="noopener noreferrer"
                            sx={{
                              backgroundColor: "#f1f5f9",
                              color: "#0369a1",
                              "&:hover": {
                                backgroundColor: "#e0f2fe",
                                color: "#0284c7",
                              },
                            }}
                          >
                            {icon}
                          </IconButton>
                        </Tooltip>
                      ) : null
                    )}
                  </Box>
                </Grid>
              )}

              {/* QR Code */}
              {user.qrLink && (
                <Grid item xs={12}>
                  <Typography
                    variant="subtitle2"
                    sx={{
                      color: "#94a3b8",
                      fontWeight: 600,
                      textTransform: "uppercase",
                      letterSpacing: 0.5,
                      mb: 1.5,
                    }}
                  >
                    QR Code
                  </Typography>
                  <Box
                    sx={{
                      mt: 1,
                      p: 2,
                      display: "inline-block",
                      backgroundColor: "#fff",
                      border: "1px solid #e2e8f0",
                      borderRadius: 3,
                      boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.05)",
                    }}
                  >
                    <img
                      src={user.qrLink}
                      alt="QR Code"
                      style={{
                        maxWidth: 150,
                        height: "auto",
                        display: "block",
                      }}
                    />
                  </Box>
                </Grid>
              )}
            </Grid>
          </CardContent>
        </Card>
      </Box>
    </Box>
  );
};

export default VisitorProfileDetail;
